/**
 * The variant a `message:<slotId>` thread was started with, remembered per slot from its first send
 * so later sends keep carrying the SAME `messageSlot` even if the slot leaves the projection (an
 * operator deleted it, a SIGNED_IN-only slot after logout). Instance storage: memory for
 * `session="isolated"` / preview. Best-effort: storage may throw.
 */
import type { VisitorStorage } from "../visitor-storage";
import type { SlotPick } from "./slot-thread";

export const slotPickKey = (pk: string, slotId: string) => `wexio:slot-thread:${pk}:${slotId}`;

export function readSlotPick(storage: VisitorStorage, pk: string, slotId: string): SlotPick | null {
  try {
    const raw = storage.getItem(slotPickKey(pk, slotId));
    if (!raw) return null;
    const o: unknown = JSON.parse(raw);
    const variantId = (o as { variantId?: unknown } | null)?.variantId;
    return typeof variantId === "string" && variantId ? { slotId, variantId } : null;
  } catch {
    return null;
  }
}

/** First pick wins: the thread was seeded with it. */
export function rememberSlotPick(storage: VisitorStorage, pk: string, pick: SlotPick) {
  if (readSlotPick(storage, pk, pick.slotId)) return;
  try {
    storage.setItem(slotPickKey(pk, pick.slotId), JSON.stringify({ variantId: pick.variantId }));
  } catch {
    // Blocked storage: the thread still works for this mount.
  }
}
